/**
 * Página de Política de Cookies - Estilo Awwwards
 */

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { useTranslation, Trans } from 'react-i18next';
import { AnimatedSection } from '../components/animations';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de Política de Cookies con estilo Awwwards
 * @returns {JSX.Element} Página de política de cookies inmersiva
 */
const CookiesPage = () => {
  const { t } = useTranslation('legal');
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 50, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-end pt-28 md:pt-32 pb-24 md:pb-32 overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen en patrón repetido (tile) */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/banners/banner-negro.webp)',
            backgroundSize: '350px auto',
            backgroundRepeat: 'repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl">
            <h1 className="hero-title font-baskerville font-bold text-white text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] mb-8 drop-shadow-lg">
              <Trans
                i18nKey="cookies.hero.title"
                ns="legal"
                components={{ hl: <span className="text-intercasa-yellow" /> }}
              />
            </h1>
            <p className="text-lg md:text-xl text-white/60 max-w-2xl font-light">
              {t('cookies.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contenido */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('cookies.what.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('cookies.what.text')}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-8">
                  {t('cookies.types.title')}
                </h2>

                <div className="space-y-6">
                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      {t('cookies.types.necessary.title')}
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      {t('cookies.types.necessary.text')}
                    </p>
                  </div>

                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      {t('cookies.types.functional.title')}
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      {t('cookies.types.functional.text')}
                    </p>
                  </div>

                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      {t('cookies.types.analytics.title')}
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      {t('cookies.types.analytics.text')}
                    </p>
                  </div>

                  <div className="bg-intercasa-bone p-8 rounded-2xl border-l-4 border-intercasa-red">
                    <h3 className="font-baskerville text-2xl font-bold text-intercasa-dark mb-3">
                      {t('cookies.types.advertising.title')}
                    </h3>
                    <p className="text-intercasa-gray text-lg leading-relaxed">
                      {t('cookies.types.advertising.text')}
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="mb-16">
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('cookies.manage.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('cookies.manage.text')}
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div>
                <h2 className="font-baskerville text-3xl md:text-4xl font-bold text-intercasa-dark mb-6">
                  {t('cookies.more.title')}
                </h2>
                <p className="text-intercasa-gray text-lg leading-relaxed">
                  {t('cookies.more.text')}
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default CookiesPage;
